import React from 'react'
import { detailedTagAxes, TagAxisId, tagAxesObject, TagAxis, tagMap } from '@cpsy-rmap-project/tags'
import Box from '@material-ui/core/Box'
import Checkbox from '@material-ui/core/Checkbox'
import FormControl from '@material-ui/core/FormControl'
import FormControlLabel from '@material-ui/core/FormControlLabel'
import InputLabel from '@material-ui/core/InputLabel'
import ListItemText from '@material-ui/core/ListItemText'
import MenuItem from '@material-ui/core/MenuItem'
import Select from '@material-ui/core/Select'
import Switch from '@material-ui/core/Switch'
import { compact, isEqual } from 'lodash'
import styled from 'styled-components'
import { Colors } from '../../../consts/Colors'

const StyledMenuItem = styled(MenuItem)`
  min-width: 260px;
`

export type HandleSelectTagsOption = string[]

export interface MultipleSelectTagsProps {
  tagAxisId: TagAxisId
  selectTags: Record<TagAxis['id'], string[]>
  handleSelectTags: (tagAxis: TagAxis['id'], options: HandleSelectTagsOption) => void
  disableUpspecifiedTag: boolean
}

export const MultipleSelectTags = ({
  tagAxisId,
  selectTags,
  handleSelectTags,
  disableUpspecifiedTag
}: MultipleSelectTagsProps) => {
  const [isEnableAllTags, setIsEnableAllTags] = React.useState(false)
  const [open, setOpen] = React.useState(false)
  const [isReRendering, setIsReRendering] = React.useState(false)

  const tagAxis = tagAxesObject[tagAxisId]
  const selectAxisTagIds = selectTags[tagAxisId]

  React.useEffect(() => {
    const selectAxisDetailedTags = compact(selectAxisTagIds.map(tagId => tagMap[tagId]))
    if (selectAxisDetailedTags.some(detailedTag => detailedTag.parentTagId !== undefined)) {
      // もし選択済みタグで、lv1以外を含む場合、isEnableAllTagsをオンにする
      setIsEnableAllTags(true)
    } else {
      // Popoverを閉じていたらisEnableAllTagsをオフにする
      // eslint-disable-next-line no-lonely-if
      if (!open && !isReRendering) {
        setIsEnableAllTags(false)
      }
    }
  }, [selectAxisTagIds, open, isReRendering])

  return (
    <FormControl style={{ width: '150px', margin: '0 10px' }}>
      <InputLabel shrink id={`${tagAxisId}-select-label`} style={{ fontSize: '14px' }}>
        {tagAxis.name}
      </InputLabel>
      <Select
        labelId={`${tagAxisId}-select-label`}
        value={selectTags[tagAxisId] || ''}
        renderValue={selected => {
          const selectedTagIds = selected as string[]
          return selectedTagIds.map(id => tagMap[id]?.name).join(', ')
        }}
        displayEmpty
        multiple
        onChange={e => {
          const currentSelectedTagIds = compact(e.target.value as Array<string | undefined>)
          if (!isEqual(currentSelectedTagIds, selectAxisTagIds)) {
            // 変更がある場合のみhandleSelectTags
            handleSelectTags(tagAxisId, currentSelectedTagIds)
          }
        }}
        onClick={() => {
          if (!open) {
            setOpen(true)
          }
        }}
        MenuProps={{
          open,
          onClose: () => {
            setOpen(false)
          }
          // style: {
          //   maxHeight: 400
          // }
        }}
      >
        <Box style={{ margin: '10px 10px', display: 'flex', justifyContent: 'flex-end' }}>
          <FormControlLabel
            control={
              <Switch
                color="primary"
                checked={isEnableAllTags}
                onChange={e => {
                  setIsReRendering(true)
                  setOpen(false) // 一度閉じる
                  setIsEnableAllTags(e.target.checked)
                  setTimeout(() => {
                    setOpen(true)
                    setIsReRendering(false)
                  }) // 時間差で開いてレンダーを発動させる（全てのMenuが表示出来ない問題の解消）
                }}
              />
            }
            label="expand all tags in filter"
            style={{ color: Colors.Primary.main }}
          />
        </Box>
        {detailedTagAxes[tagAxisId].map(detailedTag => (
          <StyledMenuItem
            key={detailedTag.id}
            value={detailedTag.id}
            disabled={
              /**
               * Hide unspecified tag用
               */
              (disableUpspecifiedTag && detailedTag.name === 'Unspecified') ||
              //
              /**
               * 親タグが選択されていたら無効化する
               */
              // 自分自身ではない
              (detailedTag.id !== detailedTag.ancestorTagId &&
                // 先祖タグが選択済み
                (selectTags[tagAxisId].includes(detailedTag.ancestorTagId) ||
                  // 親タグが選択済み
                  (detailedTag.parentTagId ? selectTags[tagAxisId].includes(detailedTag.parentTagId) : false)))
            }
            style={
              // isEnableAllTagsがfalseで小タグの場合はdisplayをnoneにする
              !isEnableAllTags && detailedTag.parentTagId !== undefined ? { display: 'none' } : {}
            }
          >
            <Checkbox
              checked={selectAxisTagIds.indexOf(detailedTag.id) > -1}
              style={{ marginLeft: 32 * (detailedTag.depth - 1) }}
            />
            <ListItemText primary={detailedTag.name} />
          </StyledMenuItem>
        ))}
      </Select>
    </FormControl>
  )
}
